import React, { useState, useMemo } from 'react';
import { ChangeLogEntry } from '../lib/changeLog';
import { exportCsv } from '../lib/changeLog';

interface Props {
  entries: ChangeLogEntry[];
}

const ACTION_STYLE: Record<string, string> = {
  CREATE: 'bg-gecko/15 text-gecko',
  UPDATE: 'bg-sky-100 text-sky-700',
  DELETE: 'bg-red-100 text-red-600',
  RESTORE: 'bg-orange-100 text-orange-600',
};

const ChangeLogView: React.FC<Props> = ({ entries }) => {
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  const entities = useMemo(() => Array.from(new Set(entries.map(e => e.entity))).sort(), [entries]);

  const filtered = entries.filter(e =>
    (entityFilter === 'all' || e.entity === entityFilter) &&
    (actionFilter === 'all' || e.action === actionFilter)
  );

  const doExport = () => {
    exportCsv(
      `change-log-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Timestamp', 'User', 'Role', 'Action', 'Entity', 'Entity ID', 'Label', 'Summary', 'Changed Fields'],
      filtered.map(e => [
        e.at, e.who, e.whoRole, e.action, e.entity, e.entityId,
        e.entityLabel || '', e.summary, (e.changedFields || []).join('; '),
      ])
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-bark">Change Log</h2>
          <p className="text-stone-500 text-sm mt-1">Every create, edit and delete across the sales &amp; invoice flow is recorded here — who, what, when.</p>
        </div>
        <button onClick={doExport} className="px-4 py-2 bg-stone-100 text-stone-600 rounded-lg font-bold text-sm hover:bg-stone-200 transition">Export CSV</button>
      </div>

      <div className="flex items-center space-x-4">
        <select value={entityFilter} onChange={e => setEntityFilter(e.target.value)}
          className="px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-600">
          <option value="all">All record types</option>
          {entities.map(en => <option key={en} value={en}>{en}</option>)}
        </select>
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
          className="px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-600">
          <option value="all">All actions</option>
          <option value="CREATE">Create</option>
          <option value="UPDATE">Update</option>
          <option value="DELETE">Delete</option>
          <option value="RESTORE">Restore</option>
        </select>
        <span className="text-xs font-bold text-stone-400">{filtered.length} of {entries.length} entries</span>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">When</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Who</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Action</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Record</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Summary</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-stone-400 font-bold italic">No changes recorded yet.</td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm font-bold text-stone-700">{new Date(e.at).toLocaleDateString()}</p>
                    <p className="text-[10px] text-stone-400">{new Date(e.at).toLocaleTimeString()}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-bold text-bark">{e.who}</p>
                    {e.whoRole && <p className="text-[10px] text-stone-400 uppercase">{e.whoRole}</p>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${ACTION_STYLE[e.action] || 'bg-stone-100 text-stone-500'}`}>{e.action}</span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-bold text-stone-700 capitalize">{e.entity}</p>
                    <p className="text-[10px] font-mono text-stone-400">{e.entityId.slice(0, 8)}…{e.entityLabel ? ' · ' + e.entityLabel : ''}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {e.summary}
                    {e.changedFields && e.changedFields.length > 0 && (
                      <p className="text-[10px] text-stone-400 mt-1">Changed: {e.changedFields.join(', ')}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ChangeLogView;
